import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import { Job, useDeleteReport } from '@ais-ui/websockets';
import React, { useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';

interface ReportDeleteProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  job: Job;
  onBackToReports: () => void;
}

export const ReportDelete = ({
  showDialog,
  setShowDialog,
  job,
  onBackToReports,
}: ReportDeleteProps) => {
  const { messages } = useLocalization();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate } = useDeleteReport();
  const { toast } = useToast();

  const onDelete = (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    const reportName = job.name;

    if (!job.uuid) {
      toast({
        className: 'delete-report-error-toast',
        title: messages.genericToastTitleError,
        description: messages.reportsDeleteFailedNoIdMessage.replace(
          '{label}',
          reportName
        ),
        variant: 'destructive',
      });
      setIsSubmitting(false);
      setShowDialog(false);
      return;
    }

    mutate(job.uuid, {
      onSuccess: () => {
        toast({
          className: 'delete-report-success-toast',
          title: messages.genericToastTitleSuccess,
          description: messages.reportsDeleteSuccessMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
        onBackToReports();
      },
      onError: (error: any) => {
        if (error.response?.status === 403) {
          toast({
            className: 'delete-report-error-toast',
            title: messages.genericToastTitleForbidden,
            description:
              messages.reportsDeleteFailedMissingPermissionsMessage.replace(
                '{label}',
                reportName
              ),
            variant: 'destructive',
          });
          setIsSubmitting(false);
          setShowDialog(false);
        } else {
          toast({
            className: 'delete-report-error-toast',
            title: messages.genericToastTitleError,
            description: messages.reportsDeleteFailedGenericMessage.replace(
              '{label}',
              reportName
            ),
            variant: 'destructive',
          });
          setIsSubmitting(false);
          setShowDialog(false);
        }
      },
    });
  };

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent id="delete-report-dialog">
        <DialogHeader className="space-y-4">
          <DialogTitle>{messages.reportsDeleteTitle}</DialogTitle>
          <DialogDescription>{messages.reportsDeleteWarning}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onDelete}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.reportsDeleteNameLabel}
            </Label>
            <Label htmlFor="report-name">{job.name}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={isSubmitting}
            >
              {messages.genericCancel}
            </Button>
            <Button
              onClick={(event) => onDelete(event)}
              variant="destructive"
              id="report-confirm-delete-button"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? messages.genericDeleting
                : messages.genericDelete}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
